import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Stack, Avatar, Chip, Button, Tabs, Tab, Divider, Accordion,
  AccordionSummary, AccordionDetails, useMediaQuery, useTheme
} from "@mui/material";
import {
  RocketLaunch, Security, Devices, LocalOffer, Storefront, BarChart,
  PhoneAndroid, Loyalty, CampaignOutlined, DeliveryDining, TrendingUp,
  LocalShipping, SmartToy, Groups, StarRate, AdminPanelSettings,
  ExpandMore, CheckCircleOutline, ArrowForward, Restaurant, LocalHospital
} from "@mui/icons-material";
import { Link } from "react-router-dom";

const accent = "#0d47a1";
const accentLight = "#e3f2fd";
const accentDark = "#082e6a";

const modules = [
  {
    icon: <RocketLaunch />, num: 1, title: "Client Onboarding Form",
    target: "Both", priority: "Phase 1",
    desc: "Multi-step guided setup capturing business identity, hours, payment gateway, brand assets, and AI voice configuration. Drives automatic website and POS go-live.",
    features: [
      "Business identity & registration capture",
      "Operating hours & holiday scheduling",
      "Payment gateway selection & connection",
      "Brand assets upload (logo, colours, fonts)",
      "AI voice agent configuration",
      "One-click Go Live activation",
      "Auto-generates website, POS pairing & GA4",
      "Client welcome email dispatch",
    ],
  },
  {
    icon: <Security />, num: 2, title: "Multi-Payment Gateway System",
    target: "Both", priority: "Phase 1",
    desc: "Payment abstraction layer supporting Stripe, Square, PayPal/Braintree, Adyen, and Worldpay. Businesses choose their preferred gateway; the platform routes transactions accordingly.",
    features: [
      "Stripe integration with auto-payout",
      "Square terminal & online payments",
      "PayPal / Braintree checkout",
      "Adyen multi-currency support",
      "Worldpay for enterprise merchants",
      "Unified transaction dashboard",
      "PCI-DSS compliant processing",
      "DTMF telephonic card entry via voice",
    ],
  },
  {
    icon: <Devices />, num: 3, title: "Own POS Hardware & Software",
    target: "Restaurant", priority: "Phase 1",
    desc: "Platform-provided Android POS terminal pre-loaded with custom software. Includes Kitchen Display System (KDS), real-time menu sync, end-of-day reporting, and coexistence with Square/Clover/Toast.",
    features: [
      "Pre-configured Android POS terminal",
      "Kitchen Display System (KDS)",
      "Real-time menu & price sync",
      "End-of-day reports & cash reconciliation",
      "Coexistence with Square, Clover & Toast",
      "Offline mode with auto-sync",
      "Staff shift management",
      "Order acceptance & status updates",
    ],
  },
  {
    icon: <LocalOffer />, num: 4, title: "Menu Management & Deals Engine",
    target: "Restaurant", priority: "Phase 1",
    desc: "Full menu control: items, modifiers, combos, daily specials, pricing floors, and dynamic promotions. Restaurant fully owns all deals — platform promotions respect set price floors.",
    features: [
      "Menu items with images & descriptions",
      "Modifier groups (toppings, sizes, etc.)",
      "Combo meals & bundle deals",
      "Daily specials with scheduling",
      "Price floors to protect margins",
      "Dynamic promotions & discount codes",
      "Category & subcategory organisation",
      "Bulk import/export support",
    ],
  },
  {
    icon: <Storefront />, num: 5, title: "Online Ordering, Booking & Aggregators",
    target: "Both", priority: "Phase 1",
    desc: "Direct online ordering (zero commission) for restaurants and appointment booking for healthcare. Unified dashboard integrates Uber Eats, DoorDash, and Just Eat orders alongside direct orders.",
    features: [
      "Zero-commission direct ordering",
      "Real-time appointment booking",
      "Uber Eats order integration",
      "DoorDash order integration",
      "Just Eat order integration",
      "Unified order dashboard with source badges",
      "Order accept/reject from single screen",
      "Customer order tracking page",
    ],
  },
  {
    icon: <BarChart />, num: 6, title: "Website Builder (Auto-Generated)",
    target: "Both", priority: "Phase 1",
    desc: "Auto-generated branded website with SEO engine, GA4 integration, content management, and credit-based redesigns.",
    features: [
      "Auto-generated from brand assets",
      "Built-in SEO engine with schema markup",
      "Google Analytics 4 integration",
      "Content management (no credit needed)",
      "Local SEO with Google Maps",
      "Mobile-responsive design",
      "Custom domain support",
      "Credit-based redesigns",
    ],
  },
  {
    icon: <PhoneAndroid />, num: 7, title: "Branded Customer Mobile App",
    target: "Both", priority: "Phase 2",
    desc: "White-label iOS and Android apps generated from brand assets. Restaurant modules: ordering, loyalty, combos, specials. Healthcare modules: booking and appointments. Push notification engine included.",
    features: [
      "White-label iOS app",
      "White-label Android app",
      "Ordering & booking modules",
      "Loyalty & rewards integration",
      "Push notification engine",
      "Combo & special deal showcases",
      "In-app payment processing",
      "Auto-generated from brand assets",
    ],
  },
  {
    icon: <Loyalty />, num: 8, title: "Loyalty & Rewards Program",
    target: "Both", priority: "Phase 2",
    desc: "Points earn and redeem on every direct order or appointment. Multi-tier system (Bronze/Silver/Gold) with automatic upgrades and earn multipliers. Digital gift cards purchaseable and redeemable.",
    features: [
      "Points on every direct transaction",
      "Bronze / Silver / Gold tiers",
      "Automatic tier upgrades",
      "Earn multiplier campaigns",
      "Digital gift card purchase & redemption",
      "Points balance in app & website",
      "Tier-based exclusive perks",
      "Referral bonus programs",
    ],
  },
  {
    icon: <CampaignOutlined />, num: 9, title: "Marketing Automation",
    target: "Both", priority: "Phase 2",
    desc: "Automated email and RCS campaigns with ChatGPT-generated content. Pre-built campaigns: Welcome, Win-Back, Birthday, Seasonal. Behavioural trigger engine with visual workflow builder and dynamic segmentation.",
    features: [
      "ChatGPT-powered content generation",
      "Email & RCS campaign channels",
      "Welcome series automation",
      "Win-back inactive customers",
      "Birthday & anniversary campaigns",
      "Seasonal promotion templates",
      "Visual workflow builder",
      "Dynamic customer segmentation",
    ],
  },
  {
    icon: <DeliveryDining />, num: 10, title: "Driver App",
    target: "Restaurant", priority: "Phase 2",
    desc: "Native iOS/Android driver app for delivery management. Includes direct-order QR promotion: drivers show customers a QR code to incentivise ordering directly next time, with driver bonus for conversions.",
    features: [
      "Native iOS & Android app",
      "Real-time order assignment",
      "GPS navigation integration",
      "QR code for direct-order promotion",
      "Driver bonus for conversions",
      "Delivery status updates",
      "Earnings & performance dashboard",
      "Shift scheduling",
    ],
  },
  {
    icon: <TrendingUp />, num: 11, title: "Customer Acquisition & Conversion",
    target: "Restaurant", priority: "Phase 2",
    desc: "Strategy and tooling to convert marketplace customers to direct customers. Break-even calculator, conversion funnel analytics, coexistence mode, and ROI dashboard showing money saved vs marketplace commissions.",
    features: [
      "Marketplace-to-direct conversion tools",
      "Break-even calculator",
      "Conversion funnel analytics",
      "Coexistence mode with aggregators",
      "ROI dashboard (saved vs. commissions)",
      "Customer acquisition cost tracking",
      "Direct-order incentive campaigns",
      "Commission comparison reports",
    ],
  },
  {
    icon: <LocalShipping />, num: 12, title: "Delivery & Logistics Management",
    target: "Restaurant", priority: "Phase 2",
    desc: "Delivery zone configuration on Google Maps. Dispatch to own drivers via Driver App or DoorDash Drive fallback. Real-time GPS tracking link sent to customer via RCS on dispatch.",
    features: [
      "Google Maps delivery zone config",
      "Own driver dispatch",
      "DoorDash Drive fallback",
      "Real-time GPS tracking for customers",
      "RCS tracking link on dispatch",
      "Delivery time estimates",
      "Zone-based pricing rules",
      "Driver assignment optimisation",
    ],
  },
  {
    icon: <SmartToy />, num: 13, title: "AI Voice Agent (ElevenLabs + Twilio)",
    target: "Both", priority: "Phase 3",
    desc: "AI answers all inbound calls using ElevenLabs voice. Detects intent via ChatGPT NLU: orders, bookings, FAQ, status checks, and escalation to human staff. Full call recording, transcription, and analytics dashboard.",
    features: [
      "ElevenLabs natural voice synthesis",
      "ChatGPT NLU intent detection",
      "Phone order taking",
      "Appointment booking by phone",
      "FAQ answering",
      "Order status checks",
      "Escalation to human staff",
      "Call recording & transcription",
      "Voice analytics dashboard",
      "90-day retention with PII redaction",
    ],
  },
  {
    icon: <Groups />, num: 14, title: "CRM, Analytics & Reviews",
    target: "Both", priority: "Phase 2",
    desc: "360-degree customer profiles linking orders, bookings, loyalty, RCS history, and voice transcripts. Unified revenue dashboard with direct vs marketplace comparison. Automated RCS review requests with Google Review routing.",
    features: [
      "360° customer profiles",
      "Order & booking history timeline",
      "Loyalty & RCS communication log",
      "Voice transcript integration",
      "Revenue dashboard (direct vs marketplace)",
      "Automated review requests via RCS",
      "4-5★ routed to Google Reviews",
      "Lower ratings captured privately",
    ],
  },
  {
    icon: <AdminPanelSettings />, num: 15, title: "Admin Panel & Platform Architecture",
    target: "Both", priority: "Phase 1",
    desc: "Super admin panel with category-driven architecture. Add new business types (Retail, Salon, Gym) with no code changes. Manages all clients, subscriptions, credit allocation, and platform-wide settings.",
    features: [
      "Super admin dashboard",
      "Category-driven business types",
      "Client management & onboarding oversight",
      "Subscription plan management",
      "Credit allocation system",
      "Feature flag controls",
      "Platform-wide analytics",
      "No-code industry expansion",
    ],
  },
  {
    icon: <StarRate />, num: 16, title: "Decision Log & Documentation",
    target: "Both", priority: "Ongoing",
    desc: "Documents all decisions made between V1 and V2: POS module addition, multi-gateway requirement, driver app, customer acquisition strategy, enhanced onboarding, and flexible category architecture.",
    features: [
      "Version-controlled decision log",
      "Architecture decision records",
      "Client meeting notes integration",
      "Scope change tracking",
      "Feature rationale documentation",
      "Priority classification system",
    ],
  },
];

const getTargetColor = (target) => {
  if (target === "Restaurant") return { bg: "#fff3e0", text: "#e65100" };
  if (target === "Healthcare") return { bg: "#f3e5f5", text: "#7b1fa2" };
  return { bg: "#e8f5e9", text: "#2e7d32" };
};

const ServicesPage = () => {
  const [filter, setFilter] = useState("All");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const filtered = filter === "All" ? modules : modules.filter((m) =>
    m.target === filter || m.target === "Both"
  );

  return (
    <>
      <Helmet>
        <title>Services — 16 Modules & 74 Features | Rite Marketing Automation</title>
        <meta name="description" content="Explore all 16 modules and 74 features of the Rite Marketing Automation Platform — from POS & online ordering to AI voice agents, loyalty programs, CRM, and marketing automation." />
        <meta name="keywords" content="marketing automation features, restaurant POS, healthcare booking, AI voice agent, loyalty program, CRM analytics, online ordering, delivery management" />
        <link rel="canonical" href="https://riteclinic.com/services" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Rite Platform Modules",
          "numberOfItems": 16,
          "itemListElement": modules.map((m, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": m.title,
            "description": m.desc,
          })),
        })}</script>
      </Helmet>

      {/* ===== HERO ===== */}
      <Box sx={{
        background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
        color: "#fff", py: { xs: 10, md: 14 },
      }}>
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Chip label="Full Platform Overview" sx={{
            bgcolor: "rgba(255,255,255,0.15)", color: "#fff", fontWeight: 600,
            mb: 3, fontSize: "0.85rem",
          }} />
          <Typography variant="h1" sx={{
            fontSize: { xs: "2rem", md: "3rem" }, fontWeight: 800, mb: 3, lineHeight: 1.2,
          }}>
            16 Modules.{" "}
            <Box component="span" sx={{ color: accentLight }}>74 Features.</Box>
            <br />One Powerful Platform.
          </Typography>
          <Typography sx={{
            color: "rgba(255,255,255,0.8)", fontSize: "1.1rem", lineHeight: 1.7, maxWidth: 620, mx: "auto",
          }}>
            Every module is independently scoped yet deeply integrated — shared customer profiles,
            unified payments, and a single analytics engine powering everything.
          </Typography>
        </Container>
      </Box>

      {/* ===== FILTER TABS ===== */}
      <Box sx={{ bgcolor: accentLight, py: 2, position: "sticky", top: 0, zIndex: 10 }}>
        <Container maxWidth="md">
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
            {["All", "Restaurant", "Healthcare"].map((f) => (
              <Button key={f} variant={filter === f ? "contained" : "outlined"} size="small"
                startIcon={f === "Restaurant" ? <Restaurant /> : f === "Healthcare" ? <LocalHospital /> : null}
                onClick={() => setFilter(f)}
                sx={{
                  bgcolor: filter === f ? accent : "transparent",
                  color: filter === f ? "#fff" : accent,
                  borderColor: accent,
                  fontWeight: 600, borderRadius: 2, px: 3,
                  "&:hover": { bgcolor: filter === f ? accentDark : `${accent}10` },
                  mb: 1,
                }}
              >
                {f}
              </Button>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ===== DELIVERY PHASES SUMMARY ===== */}
      <Box sx={{ py: 6, bgcolor: "#fff" }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" sx={{
            fontSize: { xs: "1.6rem", md: "2rem" }, fontWeight: 800, color: accent, mb: 4,
          }}>
            Delivery Roadmap
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                phase: "Phase 1 — MVP",
                desc: "Onboarding, Payments, POS, Ordering, Booking, Website Builder, Admin Panel",
                color: "#2e7d32", bg: "#e8f5e9",
              },
              {
                phase: "Phase 2 — Growth",
                desc: "Mobile App, Loyalty, Marketing Automation, Driver App, CRM, Analytics, Delivery, Customer Acquisition",
                color: "#e65100", bg: "#fff3e0",
              },
              {
                phase: "Phase 3 — AI",
                desc: "AI Voice Agent with ElevenLabs + ChatGPT — 24/7 automated call handling",
                color: "#7b1fa2", bg: "#f3e5f5",
              },
            ].map((p, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box sx={{
                  p: 3, borderRadius: 2, bgcolor: p.bg, border: `1px solid ${p.color}30`,
                  height: "100%",
                }}>
                  <Typography sx={{ fontWeight: 700, color: p.color, mb: 1 }}>{p.phase}</Typography>
                  <Typography sx={{ color: "#555", fontSize: "0.9rem", lineHeight: 1.6 }}>{p.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Divider />

      {/* ===== MODULE DETAILS ===== */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: "#fafafa" }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" sx={{
            fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 800, color: accent, mb: 6,
          }}>
            Detailed Module Breakdown
          </Typography>
          <Stack spacing={3}>
            {filtered.map((m, i) => {
              const tc = getTargetColor(m.target);
              return (
                <Accordion key={i} elevation={0} sx={{
                  border: "1px solid #e0e0e0", borderRadius: "12px !important",
                  "&:before": { display: "none" },
                  overflow: "hidden",
                }}>
                  <AccordionSummary expandIcon={<ExpandMore sx={{ color: accent }} />}
                    sx={{
                      px: 3, py: 1,
                      "&:hover": { bgcolor: accentLight },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
                      <Avatar sx={{ bgcolor: accentLight, color: accent, width: 44, height: 44 }}>
                        {m.icon}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography sx={{ fontWeight: 700, color: "#222", fontSize: "1.05rem" }}>
                            Module {m.num}: {m.title}
                          </Typography>
                          <Chip label={m.target} size="small" sx={{
                            bgcolor: tc.bg, color: tc.text, fontWeight: 600, fontSize: "0.7rem",
                          }} />
                          <Chip label={m.priority} size="small" variant="outlined" sx={{
                            borderColor: accent, color: accent, fontWeight: 600, fontSize: "0.7rem",
                          }} />
                        </Stack>
                        <Typography sx={{ color: "#888", fontSize: "0.85rem", mt: 0.5, display: { xs: "none", md: "block" } }}>
                          {m.desc.substring(0, 100)}…
                        </Typography>
                      </Box>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, pb: 3 }}>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, mb: 3 }}>
                      {m.desc}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: accent, mb: 2, fontSize: "0.95rem" }}>
                      Key Features:
                    </Typography>
                    <Grid container spacing={1.5}>
                      {m.features.map((f, j) => (
                        <Grid item xs={12} sm={6} key={j}>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <CheckCircleOutline sx={{ fontSize: 18, color: accent, mt: 0.3 }} />
                            <Typography sx={{ fontSize: "0.88rem", color: "#444", lineHeight: 1.5 }}>
                              {f}
                            </Typography>
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Container>
      </Box>

      {/* ===== INTEGRATIONS ===== */}
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: "#fff" }}>
        <Container maxWidth="lg">
          <Typography variant="h2" textAlign="center" sx={{
            fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 800, color: accent, mb: 2,
          }}>
            Enterprise-Grade Integrations
          </Typography>
          <Typography textAlign="center" sx={{ color: "#666", maxWidth: 550, mx: "auto", mb: 5 }}>
            Best-in-class third-party services powering every aspect of the platform.
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {[
              { name: "Stripe", cat: "Payments" },
              { name: "Square", cat: "Payments & POS" },
              { name: "PayPal", cat: "Payments" },
              { name: "Adyen", cat: "Payments" },
              { name: "Worldpay", cat: "Payments" },
              { name: "ElevenLabs", cat: "AI Voice" },
              { name: "ChatGPT", cat: "NLU & Content" },
              { name: "Twilio", cat: "Communications" },
              { name: "Google Maps", cat: "Location & Delivery" },
              { name: "GA4", cat: "Analytics" },
              { name: "Uber Eats API", cat: "Aggregator" },
              { name: "DoorDash API", cat: "Aggregator" },
              { name: "Just Eat API", cat: "Aggregator" },
              { name: "Zoom / Teams", cat: "Telehealth" },
            ].map((int, i) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
                <Box sx={{
                  p: 2, borderRadius: 2, border: "1px solid #e0e0e0", textAlign: "center",
                  transition: "all 0.3s", height: "100%",
                  "&:hover": { borderColor: accent, bgcolor: accentLight },
                }}>
                  <Typography sx={{ fontWeight: 700, color: "#222", fontSize: "0.9rem" }}>{int.name}</Typography>
                  <Typography sx={{ color: "#999", fontSize: "0.72rem" }}>{int.cat}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== COMPLIANCE ===== */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: "#fafafa" }}>
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: accent, mb: 3, fontSize: { xs: "1.4rem", md: "1.8rem" } }}>
            Security & Compliance Built In
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            {["GDPR Compliant", "HIPAA Ready", "PCI-DSS Level 1", "PII Auto-Redaction", "Data Portability", "90-Day Retention"].map((c) => (
              <Chip key={c} icon={<Security />} label={c} sx={{
                bgcolor: accentLight, color: accent, fontWeight: 600, px: 1, mb: 1,
                "& .MuiChip-icon": { color: accent },
              }} />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ===== CTA ===== */}
      <Box sx={{
        py: { xs: 8, md: 10 },
        background: `linear-gradient(135deg, ${accent}, ${accentDark})`,
        color: "#fff", textAlign: "center",
      }}>
        <Container maxWidth="sm">
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: "1.6rem", md: "2.2rem" } }}>
            Ready to Transform Your Business?
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.8)", mb: 4, lineHeight: 1.7 }}>
            Join the platform that gives you complete control over your customers,
            revenue, and brand — with zero commission.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
            <Button variant="contained" size="large" component={Link} to="/client-signup"
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: "#fff", color: accent, fontWeight: 700,
                px: 5, py: 1.5, borderRadius: 2, fontSize: "1rem",
                "&:hover": { bgcolor: accentLight },
              }}
            >
              Start Free Trial
            </Button>
            <Button variant="outlined" size="large" component={Link} to="/about"
              sx={{
                borderColor: "rgba(255,255,255,0.4)", color: "#fff",
                px: 4, py: 1.5, borderRadius: 2, fontWeight: 600,
                "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
              }}
            >
              About Us
            </Button>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default ServicesPage;
